import React, {useMemo} from 'react'
import {FormattedMessage} from 'react-intl'
import {SponsorImageKey} from '../../types/lessonKeys'
import {
  LessonSponsorAssignment,
  SponsorInfo
} from '../../types/sponsorAssignments'
import LoadingIndicator from '../LoadingIndicator'
import SponsorCheckbox from '../SponsorCheckbox'

type Props = {
  assignments: LessonSponsorAssignment[]
  currentSponsor: SponsorInfo
  allSponsors: SponsorInfo[]
  isLoading?: boolean
  error?: Error | null
  onChange: (lessonKey: SponsorImageKey, sponsorId: string | null) => void
  onModuleChange?: (
    moduleKey: SponsorImageKey,
    lessonType: '_START' | '_COMPLETION',
    isChecked: boolean
  ) => void
}

// Fixed structure of modules and lessons in order
const LESSON_STRUCTURE = [
  {type: 'module', key: 'MODULE_ONE', name: 'Module 1 - My Goals'},
  {
    type: 'lesson',
    key: 'LESSON_ONE_ONE',
    name: 'Lesson 1.1 - Being an Entrepreneur',
    startKey: 'LESSON_ONE_ONE_START',
    completionKey: 'LESSON_ONE_ONE_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_ONE_TWO',
    name: 'Lesson 1.2 - Spend, Save & Share',
    startKey: 'LESSON_ONE_TWO_START',
    completionKey: 'LESSON_ONE_TWO_COMPLETION'
  },

  {type: 'module', key: 'MODULE_TWO', name: 'Module 2 - My Plan'},
  {
    type: 'lesson',
    key: 'LESSON_TWO_ONE',
    name: 'Lesson 2.1 - Business Plan',
    startKey: 'LESSON_TWO_ONE_START',
    completionKey: 'LESSON_TWO_ONE_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_TWO_TWO',
    name: 'Lesson 2.2 - My Team',
    startKey: 'LESSON_TWO_TWO_START',
    completionKey: 'LESSON_TWO_TWO_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_TWO_THREE',
    name: 'Lesson 2.3 - Finding my Location',
    startKey: 'LESSON_TWO_THREE_START',
    completionKey: 'LESSON_TWO_THREE_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_TWO_FOUR',
    name: 'Lesson 2.4 - The Customer',
    startKey: 'LESSON_TWO_FOUR_START',
    completionKey: 'LESSON_TWO_FOUR_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_TWO_FIVE',
    name: 'Lesson 2.5 - Customer Service',
    startKey: 'LESSON_TWO_FIVE_START',
    completionKey: 'LESSON_TWO_FIVE_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_TWO_SIX',
    name: 'Lesson 2.6 - My Expenses',
    startKey: 'LESSON_TWO_SIX_START',
    completionKey: 'LESSON_TWO_SIX_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_TWO_SEVEN',
    name: 'Lesson 2.7 - Designing a Product',
    startKey: 'LESSON_TWO_SEVEN_START',
    completionKey: 'LESSON_TWO_SEVEN_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_TWO_EIGHT',
    name: 'Lesson 2.8 - Design a Stand',
    startKey: 'LESSON_TWO_EIGHT_START',
    completionKey: 'LESSON_TWO_EIGHT_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_TWO_NINE',
    name: 'Lesson 2.9 - Find Your Twist',
    startKey: 'LESSON_TWO_NINE_START',
    completionKey: 'LESSON_TWO_NINE_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_TWO_TEN',
    name: 'Lesson 2.10 - Marketing & Advertising',
    startKey: 'LESSON_TWO_TEN_START',
    completionKey: 'LESSON_TWO_TEN_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_TWO_ELEVEN',
    name: 'Lesson 2.11 - Financial Projections',
    startKey: 'LESSON_TWO_ELEVEN_START',
    completionKey: 'LESSON_TWO_ELEVEN_COMPLETION'
  },

  {type: 'module', key: 'MODULE_THREE', name: 'Module 3 - My Stand'},
  {
    type: 'lesson',
    key: 'LESSON_THREE_ONE',
    name: 'Lesson 3.1 - Lemonade Stand Day',
    startKey: 'LESSON_THREE_ONE_START',
    completionKey: 'LESSON_THREE_ONE_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_THREE_TWO',
    name: 'Lesson 3.2 - Lemonade Stand Skills',
    startKey: 'LESSON_THREE_TWO_START',
    completionKey: 'LESSON_THREE_TWO_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_THREE_THREE',
    name: 'Lesson 3.3 - Goal Setting',
    startKey: 'LESSON_THREE_THREE_START',
    completionKey: 'LESSON_THREE_THREE_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_THREE_FOUR',
    name: 'Lesson 3.4 - Stand Setup',
    startKey: 'LESSON_THREE_FOUR_START',
    completionKey: 'LESSON_THREE_FOUR_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_THREE_FIVE',
    name: 'Lesson 3.5 - Getting Started',
    startKey: 'LESSON_THREE_FIVE_START',
    completionKey: 'LESSON_THREE_FIVE_COMPLETION'
  },

  {type: 'module', key: 'MODULE_FOUR', name: 'Module 4 - My Results'},
  {
    type: 'lesson',
    key: 'LESSON_FOUR_ONE',
    name: 'Lesson 4.1 - My Numbers',
    startKey: 'LESSON_FOUR_ONE_START',
    completionKey: 'LESSON_FOUR_ONE_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_FOUR_TWO',
    name: 'Lesson 4.2 - Spend, Save & Share',
    startKey: 'LESSON_FOUR_TWO_START',
    completionKey: 'LESSON_FOUR_TWO_COMPLETION'
  },
  {
    type: 'lesson',
    key: 'LESSON_FOUR_THREE',
    name: 'Lesson 4.3 - Thank You Letter',
    startKey: 'LESSON_FOUR_THREE_START',
    completionKey: 'LESSON_FOUR_THREE_COMPLETION'
  }
] as const

function SponsorAssignmentTable({
  assignments,
  currentSponsor,
  allSponsors,
  isLoading,
  error,
  onChange,
  onModuleChange
}: Props) {
  const assignmentMap = useMemo(() => {
    const map = new Map<string, LessonSponsorAssignment>()
    assignments.forEach(assignment => {
      map.set(assignment.lessonKey, assignment)
    })
    return map
  }, [assignments])

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <LoadingIndicator />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-600">
          <p>Failed to load sponsor assignments</p>
          <p className="text-sm text-gray-500 mt-2">{error.message}</p>
        </div>
      </div>
    )
  }

  const renderLessonCell = (lessonKey?: SponsorImageKey) => {
    if (!lessonKey) {
      return (
        <div className="text-sm flex justify-center text-grey-tertiary-20">
          N/A
        </div>
      )
    }

    const assignment = assignmentMap.get(lessonKey)
    if (!assignment) {
      return (
        <div className="text-sm flex justify-center text-grey-tertiary-20">
          N/A
        </div>
      )
    }

    const currentSponsorId = assignment.sponsorId

    // If assigned to a different sponsor, show read-only text
    if (currentSponsorId && currentSponsorId !== currentSponsor.id) {
      const assignedSponsor = allSponsors.find(s => s.id === currentSponsorId)
      return (
        <div className="text-sm py-2 text-center text-grey-tertiary-30">
          Assigned ({assignedSponsor?.name || 'Unknown'})
        </div>
      )
    }

    // If assigned to current sponsor or not assigned, show checkbox
    const isAssigned = currentSponsorId === currentSponsor.id

    return (
      <div className="flex justify-center">
        <SponsorCheckbox
          id={`${lessonKey}`}
          checked={isAssigned}
          onChange={e => {
            onChange(lessonKey, e.target.checked ? currentSponsor.id : null)
          }}
        />
      </div>
    )
  }

  const getAssignableLessonsForModule = (
    moduleKey: SponsorImageKey,
    lessonType: '_START' | '_COMPLETION'
  ): LessonSponsorAssignment[] => {
    // Map module keys to their corresponding lesson prefixes
    const moduleToLessonMap: Record<string, string> = {
      MODULE_ONE: 'LESSON_ONE_',
      MODULE_TWO: 'LESSON_TWO_',
      MODULE_THREE: 'LESSON_THREE_',
      MODULE_FOUR: 'LESSON_FOUR_'
    }

    const lessonPrefix = moduleToLessonMap[moduleKey]
    if (!lessonPrefix) return []

    // Get all lessons for this module and lesson type from our structure
    const moduleLessons = LESSON_STRUCTURE.filter(
      item =>
        item.type === 'lesson' &&
        (lessonType === '_START'
          ? 'startKey' in item && item.startKey?.startsWith(lessonPrefix)
          : 'completionKey' in item &&
            item.completionKey?.startsWith(lessonPrefix))
    )
      .map(item => {
        if (item.type === 'lesson') {
          const key =
            lessonType === '_START' ? item.startKey : item.completionKey
          return key ? assignmentMap.get(key) : null
        }
        return null
      })
      .filter(Boolean) as LessonSponsorAssignment[]

    // Return only lessons that are unassigned or assigned to current sponsor
    return moduleLessons.filter(
      lesson => !lesson.sponsorId || lesson.sponsorId === currentSponsor.id
    )
  }

  const moduleHasAssignableLessons = (
    moduleKey: SponsorImageKey,
    lessonType: '_START' | '_COMPLETION'
  ): boolean => {
    return getAssignableLessonsForModule(moduleKey, lessonType).length > 0
  }

  const renderModuleCheckbox = (
    moduleKey: SponsorImageKey,
    lessonType: '_START' | '_COMPLETION'
  ) => {
    if (!onModuleChange) return null

    const availableLessons = getAssignableLessonsForModule(
      moduleKey,
      lessonType
    )

    if (availableLessons.length === 0) {
      return (
        <div className="text-sm flex justify-center text-grey-tertiary-20">
          N/A
        </div>
      )
    }

    const assignedLessons = availableLessons.filter(
      lesson => lesson.sponsorId === currentSponsor.id
    )

    const isFullyAssigned = assignedLessons.length === availableLessons.length
    const isPartiallyAssigned =
      assignedLessons.length > 0 &&
      assignedLessons.length < availableLessons.length

    return (
      <div className="flex justify-center">
        <SponsorCheckbox
          id={`${moduleKey}_${lessonType}_module`}
          checked={isFullyAssigned}
          ref={input => {
            if (input) {
              input.indeterminate = isPartiallyAssigned
            }
          }}
          onChange={e => {
            onModuleChange(moduleKey, lessonType, e.target.checked)
          }}
        />
      </div>
    )
  }

  const renderModuleCell = (
    moduleKey: SponsorImageKey,
    lessonType: '_START' | '_COMPLETION'
  ) => {
    return (
      <td className="px-3 py-3.5">
        <div className="flex items-center justify-center gap-2">
          {moduleHasAssignableLessons(moduleKey, lessonType) && (
            <label
              htmlFor={`${moduleKey}_${lessonType}_module`}
              className="text-sm font-[700] text-grey-tertiary-30"
            >
              <FormattedMessage id="admin.sponsors.assignCommunity.selectAll" />
            </label>
          )}
          {renderModuleCheckbox(moduleKey, lessonType)}
        </div>
      </td>
    )
  }

  return (
    <div className="mt-4 max-h-[32rem] overflow-y-auto rounded-md border border-grey-tertiary-10">
      <table className="w-full">
        <thead className="sticky top-0 z-10 border-b border-grey-tertiary-10 bg-white">
          <tr>
            <th
              scope="col"
              className="bg-white py-3.5 pl-4 pr-3 text-left sm:pl-3"
            >
              Placement
            </th>
            <th scope="col" className="bg-white px-3 py-3.5 text-center">
              Landing Pages
            </th>
            <th scope="col" className="bg-white px-3 py-3.5 text-center">
              Badge Awards
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {LESSON_STRUCTURE.map(item => (
            <tr
              key={item.key}
              className={
                item.type === 'module'
                  ? 'border-y border-grey-tertiary-30 bg-blue-tertiary-30/15'
                  : 'border-t border-grey-tertiary-10'
              }
            >
              {item.type === 'module' ? (
                <>
                  <td className="font-normal py-2 pl-4 pr-3 text-left sm:pl-3">
                    {item.name}
                  </td>
                  {renderModuleCell(item.key as SponsorImageKey, '_START')}
                  {renderModuleCell(item.key as SponsorImageKey, '_COMPLETION')}
                </>
              ) : (
                <>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-left sm:pl-3">
                    {item.name}
                  </td>
                  <td className="px-3 py-3.5">
                    {renderLessonCell(
                      item.type === 'lesson'
                        ? (item.startKey as SponsorImageKey)
                        : undefined
                    )}
                  </td>
                  <td className="px-3 py-3.5">
                    {renderLessonCell(
                      item.type === 'lesson'
                        ? (item.completionKey as SponsorImageKey)
                        : undefined
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default SponsorAssignmentTable
